import { AlertTriangle, Wifi, WifiOff } from "lucide-react";
import { useNetworkStatus } from "@/context/network-context";
import { useAuthStore } from "@/lib/auth";
import { useNetworkInfo } from "@/hooks/use-network-queries";
import { Text, Group, Box } from "@mantine/core";

export function NetworkStatusBanner() {
  const { status, internalIp, isLoading } = useNetworkStatus();
  const { user } = useAuthStore();

  const { data: network } = useNetworkInfo({ enabled: status === "wrong_network" });

  if (isLoading || status === "own_network" || status === "external") {
    return null;
  }

  if (status === "wrong_network") {
    return (
      <Box style={{ backgroundColor: "var(--mantine-color-yellow-0)", borderBottom: "1px solid var(--mantine-color-yellow-3)" }}>
        <Box maw="28rem" mx="auto" px="md" py="sm">
          <Group align="flex-start" gap="sm" wrap="nowrap">
            <AlertTriangle size={20} color="var(--mantine-color-yellow-7)" style={{ marginTop: 2, flexShrink: 0 }} />
            <Box flex={1}>
              <Text fw={500} c="yellow.8">
                Connected to wrong network{internalIp ? ` \u2014 your IP: ${internalIp}` : ""}
              </Text>
              {network && (
                <Text size="sm" c="yellow.7" mt={4}>
                  Your tenant network: <strong>{network.name}</strong> (VLAN {network.vlan_id}, {network.prefix})
                </Text>
              )}
              {user?.tenant_name && (
                <Text size="sm" c="yellow.7" mt={4}>
                  You are: <strong>{user.tenant_name}</strong>
                </Text>
              )}
            </Box>
          </Group>
        </Box>
      </Box>
    );
  }

  return null;
}

export function NetworkBadge() {
  const { status, internalIp, isLoading } = useNetworkStatus();

  if (isLoading) {
    return (
      <Group gap={4} component="span" style={{ display: "inline-flex", alignItems: "center" }}>
        <Box
          component="span"
          w={8} h={8}
          style={{
            borderRadius: "50%",
            backgroundColor: "var(--mantine-color-gray-4)",
            animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
          }}
        />
        <Text size="sm" c="dimmed" component="span">Detecting...</Text>
      </Group>
    );
  }

  const ipLabel = internalIp ? ` (${internalIp})` : "";

  if (status === "own_network") {
    return (
      <Group gap={4} component="span" style={{ display: "inline-flex", alignItems: "center" }}>
        <Wifi size={16} color="var(--mantine-color-green-6)" />
        <Text size="sm" c="green.6" component="span">On-site{ipLabel}</Text>
      </Group>
    );
  }

  if (status === "wrong_network") {
    return (
      <Group gap={4} component="span" style={{ display: "inline-flex", alignItems: "center" }}>
        <AlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
        <Text size="sm" c="yellow.6" component="span">Wrong network{ipLabel}</Text>
      </Group>
    );
  }

  return (
    <Group gap={4} component="span" style={{ display: "inline-flex", alignItems: "center" }}>
      <WifiOff size={16} color="var(--mantine-color-dimmed)" />
      <Text size="sm" c="dimmed" component="span">Remote{ipLabel}</Text>
    </Group>
  );
}
